import React, { useState, useEffect } from 'react';
import { X, Plus, DollarSign, Package, AlertCircle } from 'lucide-react';
import { ForecastClient, forecastService, RecurringService, Project } from '../../lib/forecastService';
import { useToast } from '../../contexts/ToastContext';
import ServiceList from './ServiceList';
import ProjectList from './ProjectList';
import ServiceForm from './ServiceForm';
import ProjectForm from './ProjectForm';

interface ClientDetailProps {
  client: ForecastClient;
  onClose: () => void;
  onUpdate: () => void;
}

const ClientDetail: React.FC<ClientDetailProps> = ({ client, onClose, onUpdate }) => {
  const { showToast } = useToast();
  const [services, setServices] = useState<RecurringService[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [tab, setTab] = useState<'services' | 'projects'>('services');

  useEffect(() => {
    loadData();
  }, [client.id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [servicesData, projectsData] = await Promise.all([
        forecastService.getRecurringServices(client.id),
        forecastService.getProjects(client.id),
      ]);
      setServices(servicesData);
      setProjects(projectsData);
    } catch (error) {
      showToast('Failed to load client data', 'error');
      console.error('Error loading client data:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalMonthlyRecurring = services
    .filter(s => s.status === 'active')
    .reduce((sum, s) => sum + s.monthly_rate, 0);

  const totalProjectValue = projects
    .filter(p => p.status === 'active')
    .reduce((sum, p) => sum + p.total_value, 0);

  const totalProjectPaid = projects.reduce((sum, p) => sum + p.total_paid, 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{client.company_name}</h2>
            <p className="text-sm text-gray-600 mt-1">
              {client.contact_name && `${client.contact_name} • `}
              {client.contact_email}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-4 p-6 bg-gray-50 border-b border-gray-200">
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              <span className="text-sm font-medium text-gray-600">Monthly Recurring</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              ${totalMonthlyRecurring.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </p>
          </div>

          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <Package className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-gray-600">Project Value</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              ${totalProjectValue.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </p>
          </div>

          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-5 h-5 text-orange-600" />
              <span className="text-sm font-medium text-gray-600">Payment Score</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {client.payment_reliability_score}/100
            </p>
          </div>
        </div>

        <div className="flex gap-2 px-6 pt-4 border-b border-gray-200">
          <button
            onClick={() => setTab('services')}
            className={`px-4 py-2 font-medium transition-colors ${
              tab === 'services'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Recurring Services ({services.length})
          </button>
          <button
            onClick={() => setTab('projects')}
            className={`px-4 py-2 font-medium transition-colors ${
              tab === 'projects'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Projects ({projects.length})
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <p className="text-gray-500">Loading...</p>
            </div>
          ) : (
            <>
              {tab === 'services' ? (
                <>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Recurring Services</h3>
                    <button
                      onClick={() => setShowServiceForm(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Add Service
                    </button>
                  </div>
                  <ServiceList
                    services={services}
                    onRefresh={() => {
                      loadData();
                      onUpdate();
                    }}
                  />
                </>
              ) : (
                <>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">One-Time Projects</h3>
                    <button
                      onClick={() => setShowProjectForm(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Add Project
                    </button>
                  </div>
                  <ProjectList
                    projects={projects}
                    onRefresh={() => {
                      loadData();
                      onUpdate();
                    }}
                  />
                </>
              )}
            </>
          )}
        </div>
      </div>

      {showServiceForm && (
        <ServiceForm
          clientId={client.id}
          onClose={() => setShowServiceForm(false)}
          onSaved={() => {
            setShowServiceForm(false);
            loadData();
            onUpdate();
          }}
        />
      )}

      {showProjectForm && (
        <ProjectForm
          clientId={client.id}
          onClose={() => setShowProjectForm(false)}
          onSaved={() => {
            setShowProjectForm(false);
            loadData();
            onUpdate();
          }}
        />
      )}
    </div>
  );
};

export default ClientDetail;